import maxBy from 'lodash/maxBy';
import { UTCDate } from '@date-fns/utc';

import {
  GoalProgressChartData,
  RetailerNetworkPerformanceFragment,
  Retailer_Network_Performance_Period,
} from '@occc/graphql';
import { getDaysInMonth } from '@occc-app/lib/date';

import {
  ParsedChartData,
  RetailerNetworkPerformanceChartData,
  XAxisTickIntervals,
} from './retailer-network-performance-chart.types';

const getGoalForDate = (
  goal: number,
  currentDayInPeriod: number,
  period: Retailer_Network_Performance_Period,
  date: Date,
) => {
  const daysInCurrentMonth = getDaysInMonth(date);
  const daysInLastMonth = getDaysInMonth(new Date(date.getFullYear(), date.getMonth() - 1));

  const periodToDaysInPeriodMap = {
    [Retailer_Network_Performance_Period.ThisMonth]: daysInCurrentMonth,
    [Retailer_Network_Performance_Period.LastMonth]: daysInLastMonth,
    [Retailer_Network_Performance_Period.Last_7Days]: 7,
    [Retailer_Network_Performance_Period.Last_30Days]: 30,
    [Retailer_Network_Performance_Period.Last_90Days]: 90,
  };

  return Math.round(goal * (currentDayInPeriod / periodToDaysInPeriodMap[period]));
};

export const parseChartData = (
  chartData: GoalProgressChartData[],
  period: Retailer_Network_Performance_Period,
  goal: number,
): ParsedChartData[] => {
  // If the selected period is a current months (which is not finished yet),
  // we have to create additional items in array to fill the chart
  if (period === Retailer_Network_Performance_Period.ThisMonth) {
    const currentMonthIndex = new Date().getMonth();
    const daysInMonth = getDaysInMonth(new Date());
    const parsedChartData: ParsedChartData[] = [];

    for (let i = 0; i <= daysInMonth - 1; i++) {
      const currentEntryValue = chartData[i]?.value ? chartData[i]?.value : 0;
      const previousEntryValue = chartData[i - 1]?.value || 0;
      const date = chartData[i]?.timestamp
        ? new UTCDate(chartData[i].timestamp)
        : new Date(0, currentMonthIndex, i);
      const goalForDate = getGoalForDate(goal, i + 1, period, date);

      parsedChartData.push({
        totalToDate: currentEntryValue ? previousEntryValue : 0,
        dailyNew: currentEntryValue ? currentEntryValue - previousEntryValue : 0,
        date,
        goalForDate,
        progressForDate: currentEntryValue,
      });
    }

    return parsedChartData;
  }

  return chartData.map((item, index, array) => {
    const previousEntryValue = array[index - 1]?.value || 0;
    const date = new UTCDate(item.timestamp);
    const goalForDate = getGoalForDate(goal, index, period, date);

    return {
      totalToDate: item.value ? previousEntryValue : 0,
      dailyNew: item.value ? item.value - previousEntryValue : 0,
      date,
      progressForDate: item.value,
      goalForDate,
    };
  });
};

// Parse chart data into something readable by recharts and include other data insights
export const getParsedChartData = (
  chartData: GoalProgressChartData[],
  period: Retailer_Network_Performance_Period,
  goal: number,
) => {
  // If chart data not provided, return defaults
  if (!chartData?.length) {
    return {
      data: null,
      highestEntryValue: 0,
    };
  }

  // Parse existing chart data into a format recharts can use (data shape + filling empty entries)
  const parsedChartData: ParsedChartData[] = parseChartData(chartData, period, goal);
  const highestEntryValue = maxBy(parsedChartData, 'progressForDate')?.progressForDate || 0;

  return {
    data: parsedChartData,
    highestEntryValue,
  };
};

// Chart has 3 y-ticks, so all chart-formatting calculations maintain this ratio
const Y_DOMAIN_MULTIPLIER = 3;

// Rounds up to nearest number dividable by the Y_DOMAIN_MULTIPLIER
export const roundToYDomainMultiple = (num: number) => {
  // Check if number is divisible by the y-domain multipliler
  const isDivisible = num % Y_DOMAIN_MULTIPLIER === 0;
  // If not, we use the next number that is
  const domain = isDivisible ? num : Math.ceil(num / Y_DOMAIN_MULTIPLIER) * Y_DOMAIN_MULTIPLIER;
  return domain;
};

// Chooses determinant for calculating the y-domain scale (goal or highest entry value, whichever is highest)
export const getYDomainDeterminant = (
  goalDeterminant: number,
  highestValueDeterminant: number,
): number => {
  if (!goalDeterminant && !highestValueDeterminant) {
    return Y_DOMAIN_MULTIPLIER;
  }
  if (!goalDeterminant && highestValueDeterminant) {
    return highestValueDeterminant;
  }
  if (!highestValueDeterminant && goalDeterminant) {
    return goalDeterminant;
  }
  return highestValueDeterminant > goalDeterminant ? highestValueDeterminant : goalDeterminant;
};

// Calculates y-domain, accounting for tooltip space
export const getYDomainValues = (goalDeterminant: number, highestValueDeterminant: number) => {
  // Get initial domain based on goal or highest entry value, whichever is highest, then round up
  const domain = roundToYDomainMultiple(
    getYDomainDeterminant(goalDeterminant, highestValueDeterminant),
  );

  // Return new domain and unit size
  return [domain, domain / Y_DOMAIN_MULTIPLIER];
};

// Generates y-tick values based on increment scale
export const getYTickValues = (scaleRange: number, scaleUnitSize: number) => {
  const tickValues: number[] = [];
  for (let i = 1; i <= scaleRange; i++) {
    // Create a y-tick for each scale unit
    if (i % scaleUnitSize === 0) {
      tickValues.push(i);
    }
  }
  return tickValues;
};

// Returns interval from which to generate x-ticks for any number of entries
export const getXInterval = (period: Retailer_Network_Performance_Period) => {
  if (period === Retailer_Network_Performance_Period.Last_7Days) {
    return XAxisTickIntervals.Day;
  }

  if (period === Retailer_Network_Performance_Period.Last_90Days) {
    return XAxisTickIntervals.TwoWeeks;
  }

  return XAxisTickIntervals.Week;
};

export const parseRetailerNetworkPerformanceChartData = (
  retailerNetworkPerformance?: RetailerNetworkPerformanceFragment,
): RetailerNetworkPerformanceChartData | null => {
  if (!retailerNetworkPerformance) {
    return null;
  }

  return {
    newMembersChartData: retailerNetworkPerformance.newMembers.chartData,
    loyaltyTransactionsChartData: retailerNetworkPerformance.loyaltyTransactions.chartData,
    newMembersGoalProgress: {
      target: retailerNetworkPerformance.newMembers.goalProgress.goal,
      current: retailerNetworkPerformance.newMembers.goalProgress.progress,
    },
    loyaltyTransactionsGoalProgress: {
      target: retailerNetworkPerformance.loyaltyTransactions.goalProgress.goal,
      current: retailerNetworkPerformance.loyaltyTransactions.goalProgress.progress,
    },
  };
};
